import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  Box,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import InvoiceReconciliationDialog from '../../Invoicing/InvoiceReconciliationDialog';
//import InvoiceAccountingSeatWidget from './InvoiceAccountingSeatWidget';
import Autocomplete from '@mui/material/Autocomplete';
import { useNavigate, useParams } from 'react-router';
import intl from 'components/i18n/ReactIntlWrapper';
import NewInvoiceItemTable from '../../NewInvoice/NewInvoiceItemTable';
import NewInvoiceSummary from '../../NewInvoice/NewInvoiceSummary';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useInvoice } from '../../common/InvoiceProvider';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import WorkflowActionsBar from '../../Workflow/WorkflowActionsBar';
import Spinner from '../../Spinner';
import { useWorkflow } from 'components/Workflow/WorkflowContext';
import { isWorkflowStateReadOnly } from 'components/Workflow/workflowUtils';
import { handleAPIError } from '../../../common/errorHandler';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ErrorSummary from '../../common/ErrorSummary';

const AccountsPayableForm = ({ onlyView = false }) => {
  const {
    currencies,
    invoiceTypes,
    input,
    setInput,
    isManualCreation,
    errors,
    setErrors,
  } = useInvoice();
  const [statusLabel, setStatusLabel] = useState<{ label: string; color: string }>();
  const [loading, setLoading] = useState<boolean>(false);
  const [workflowLabelKeys, setWorkflowLabelKeys] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const pendingReconcileResolve = useRef<((result: boolean) => void) | null>(null);
  const [toOptions, setToOptions] = useState<{ id: number; name: string }[]>([]);
  const [toLoading, setToLoading] = useState(false);
  const [toInputValue, setToInputValue] = useState('');
  const [toValue, setToValue] = useState<{ id: number; name: string } | null>(null);
  const pendingSubmitArgs = React.useRef<{ resolve?: (value: boolean) => void } | null>(null);
  const toast = useToastMessageQueue();
  const api: Api = useApi();
  const navigate = useNavigate();
  const { id: jobId } = useParams<{ id: string }>();
  const { workflow } = useWorkflow();
  // Fields are read-only when the route is view-only OR the workflow state is non-editable.
  // Action buttons use onlyView alone — they must stay clickable regardless of field editability.
  const fieldReadOnly = onlyView || isWorkflowStateReadOnly(workflow, input.idWorkflowState);

  const searchTo = useCallback(async (term: string) => {
    setToLoading(true);
    try {
      const res = await api.invoiceApi.apiVversionPersonaEligibleInvoiceToTypeAheadGet('1', term);
      const results = (res.data?.data?.queryResult ?? [])
        .map((p) => ({ id: p.id, name: p.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setToOptions(results);
    } finally {
      setToLoading(false);
    }
  }, [api]);

  // Pre-populate options with the current persona so the Autocomplete shows it on load
  useEffect(() => {
    if (!input.idTo) return;
    if (toValue) return;
    const seed = (p: { id: number; name: string }) => {
      setToOptions([p]);
      setToValue(p);
      setToInputValue(p.name);
    };
    if (input.to) {
      seed({ id: input.idTo, name: input.to });
    } else {
      api.invoiceApi.apiVversionPersonaIdGet(input.idTo, '1').then((res) => {
        const p = res.data?.data;
        if (p?.id && p?.name) seed({ id: p.id, name: p.name });
      });
    }
  }, [input.idTo, input.to]);

  useEffect(() => {
    const newItem = {
      id: 1,
      amount: '',
      unit: 1,
      invoiceItemTaxes: [],
    };
    input.invoiceItems = [newItem];
  }, []);

  useEffect(() => {
    setWorkflowLabelKeys(
      workflow?.states?.map((state) => ({ label: state.labelKey, id: state.id }))
    );
  }, [workflow]);

  useEffect(() => {
    const currentStatus = workflowLabelKeys?.filter(
      (labelKey) => labelKey.id === input.idWorkflowState
    );
    if (!currentStatus?.[0]) {
      setStatusLabel({ label: 'invoicing.manualReview', color: 'primary' });
    } else {
      const label = currentStatus[0]?.label;
      const color =
        label === 'Pending' || label === 'Pendiente'
          ? 'warning'
          : label === 'Approved' || label === 'Aprobado'
            ? 'success'
            : label === 'Observed' || label === 'Observado'
              ? 'default'
              : label === 'Open' || label === 'Abierto'
                ? 'primary'
                : label === 'Reopen' || label === 'Reabrir'
                  ? 'secondary'
                  : 'error';
      setStatusLabel({ label, color });
    }
  }, [workflowLabelKeys, input?.idWorkflowState]);

  useEffect(() => {
    const handleAutofill = () => {
      const inputs = document.querySelectorAll('input, select');
      inputs.forEach((input) => {
        const element = input as HTMLInputElement | HTMLSelectElement;
        if (element.value && !element.dataset.initialized) {
          element.dataset.initialized = 'true';
          const event = new Event('change', { bubbles: true });
          element.dispatchEvent(event);
        }
      });
    };

    handleAutofill();
    const timeoutId = setTimeout(handleAutofill, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  // Mark a field as reviewed (clears confidence flags) without changing its value
  const handleFieldReview = (name: string) => {
    setErrors((prevErrors) => {
      if (prevErrors?.[name] === 'missing confidence' || prevErrors?.[name] === 'low confidence') {
        return { ...prevErrors, [name]: '' };
      }
      return prevErrors;
    });
  };

  const handleInputChange = (name, value, extra?: Record<string, unknown>) => {
    setInput({
      ...input,
      [name]: value,
      ...(extra ?? {}),
    });
    //validate errors
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  // Called before WorkflowActionsBar opens its comment modal.
  // Returns false to abort the action, 'handled' if the action was completed here, or true to continue.
  const handleBeforeAction = (targetStateId: number): Promise<boolean | 'handled'> => {
    // Validate required fields on editable screens before proceeding
    if (!fieldReadOnly) {
      const errorList: Record<string, string> = {};
      if (!input.number) errorList['number'] = intl.get('newInvoice.numberRequired');
      if (!input.idCurrency || input.idCurrency === 0 || input.idCurrency === '')
        errorList['idCurrency'] = intl.get('newInvoice.currencyRequired');
      if (!input.idInvoiceType || input.idInvoiceType === 0 || input.idInvoiceType === '')
        errorList['idInvoiceType'] = intl.get('newInvoice.isRequired');
      if (!input.date) errorList['date'] = intl.get('newInvoice.dateRequired');
      if (!input.addressFrom) errorList['addressFrom'] = intl.get('newInvoice.isRequired');
      if (!input.addressTo) errorList['addressTo'] = intl.get('newInvoice.isRequired');
      if (!input.idFrom || input.idFrom === 0 || input.idFrom === '')
        errorList['idFrom'] = intl.get('newInvoice.profileRequired');
      if (!input.idTo || input.idTo === 0 || input.idTo === '')
        errorList['idTo'] = intl.get('newInvoice.isRequired');
      if (!input.invoiceItems || input.invoiceItems.length === 0)
        errorList['invoiceItems'] = intl.get('newInvoice.itemsRequired');
      input.invoiceItems?.forEach((item) => {
        if (!item.description || !item.quantity)
          errorList['invoiceItems'] = 'Missing one or more field in items';
      });
      if (Object.keys(errorList).length > 0) {
        setErrors((prevErrors) => ({ ...prevErrors, ...errorList }));
        return Promise.resolve(false);
      }
    }

    // Check for unreviewed confidence fields
    const unreviewedFields = Object.entries(errors || {}).filter(
      ([, value]) => value === 'missing confidence' || value === 'low confidence'
    );
    if (unreviewedFields.length > 0) {
      setConfirmOpen(true);
      return new Promise<boolean | 'handled'>((resolve) => {
        pendingSubmitArgs.current = { resolve };
      });
    }

    // Intercept the "Paid" transition (workflowStateTo = 118)
    if (targetStateId === 118) {
      setReconcileOpen(true);
      return new Promise<boolean>((resolve) => {
        pendingReconcileResolve.current = resolve;
      });
    }

    return Promise.resolve(true);
  };

  const handleReconcileConfirm = (fullyReconciled: boolean) => {
    setReconcileOpen(false);
    if (!fullyReconciled) {
      toast.warning({ message: intl.get('accountsPayable.reconciliation.notFullyReconciled') || 'Invoice was not marked as paid because reconciliation is not 100%.' });
    }
    if (pendingReconcileResolve.current) {
      pendingReconcileResolve.current(fullyReconciled);
      pendingReconcileResolve.current = null;
    }
  };

  const handleReconcileCancel = () => {
    setReconcileOpen(false);
    if (pendingReconcileResolve.current) {
      pendingReconcileResolve.current(false);
      pendingReconcileResolve.current = null;
    }
  };

  const buildInvoiceData = (id: number, comments: string) => {
    const inputCopy = { ...input };
    inputCopy.comments = comments;

    if (inputCopy.invoiceItems) {
      inputCopy.invoiceItems = inputCopy.invoiceItems.map((item) => {
        const newItem = { ...item };
        const rate = Number(newItem.rate) || 0;
        // Send unit price (rate); backend's CalculateInvoiceTotals multiplies by quantity.
        newItem.amount = rate !== 0 ? rate : Number(newItem.amount) || 0;

        const validTaxes = (newItem.invoiceItemTaxes ?? []).filter(
          (tax) => tax.id !== '' && tax.id !== 0 && tax.id !== null
        );
        if (validTaxes.length > 0) {
          const itemAmount = newItem.amount;
          newItem.invoiceItemTaxes = validTaxes.map((tax) => ({
            idTaxRate: tax.id,
            taxAmount: tax.amount === 0 ? itemAmount * (Number(tax.rate) / 100) : Number(tax.amount),
            taxRate: Number(tax.rate) || 0,
            invoiceItemId: 0,
            invoiceItem: {},
          }));
        } else {
          newItem.invoiceItemTaxes = [];
        }
        return newItem;
      });
    }

    if (inputCopy.invoiceTaxes) {
      inputCopy.invoiceTaxes = inputCopy.invoiceTaxes
        .filter((tax) => tax.idTaxRate && tax.idTaxRate > 0 && tax.idTaxRate < 10000)
        .map((tax) => ({
          idTaxRate: tax.idTaxRate,
          taxAmount: Number(tax.taxAmount) || 0,
          taxRate: Number(tax.taxRate) || 0,
          description: tax.description ?? tax.taxRateEntity?.description ?? '',
        }));
    }

    if (inputCopy.attachments && !inputCopy.fileURI) {
      inputCopy.fileURI = inputCopy.attachments[0]?.fileURI;
    }

    return {
      ...inputCopy,
      date: new Date(inputCopy.date).toISOString().split('T')[0],
      number: inputCopy.number?.toString(),
      idInvoiceType: Number(inputCopy.idInvoiceType),
    };
  };

  // Called by WorkflowActionsBar after the comment modal is submitted.
  // Validation already ran in handleBeforeAction; this just sends the data.
  const handleSave = async (targetStateId: number, comments: string) => {
    setLoading(true);
    try {
      const invoiceData = buildInvoiceData(null, comments);
      if (workflow?.idModule === 5) {
        await api.invoiceApiWrapper.updateJobInvoiceWorkflowState('1', jobId, targetStateId, comments, invoiceData);
        navigate('/accountspayable');
        return true;
      }
      // Module 4: publish to SNS → WFStatusUpdateLambda updates all fields + state atomically.
      await api.invoiceApiWrapper.updateInvoiceWorkflowState('1', input.id, targetStateId, comments, invoiceData);
      navigate('/accountspayable/all');
      return true;
    } catch (error) {
      handleAPIError(error, toast, errors);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Save button: persists invoice data without changing workflow state.
  const handleSaveOnly = async () => {
    setLoading(true);
    try {
      if (workflow?.idModule === 5) {
        const invoiceData = buildInvoiceData(null, '');
        await api.invoiceApiWrapper.saveJobInvoiceData('1', jobId, invoiceData);
      } else {
        // Module 4: PUT /Invoice preserves idWorkflowState when we pass the current value.
        const inputCopy = buildInvoiceData(null, '');
        inputCopy.idWorkflowState = input.idWorkflowState;
        await api.invoiceApi.apiVversionInvoicePut('1', inputCopy);
      }
      toast.success({ message: intl.get('action.save') });
    } catch (error) {
      handleAPIError(error, toast, errors);
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = () => {
    return (
      !input.number ||
      !input.idCurrency ||
      !input.idInvoiceType ||
      !input.date ||
      !input.addressFrom ||
      !input.addressTo ||
      !input.idFrom ||
      !input.idTo ||
      !input.invoiceItems ||
      input.invoiceItems?.some(
        (item) =>
          !item.description ||
          item.description === '' ||
          !item.quantity ||
          item.quantity === ''
      )
    );
  };

  return (
    <>
      {loading && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.5)' }}>
          <Spinner />
        </Box>
      )}
      <ErrorSummary errors={errors} showEverything={true} />
      <Grid container spacing={2}>
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          >
            <Typography gutterBottom>{intl.get('invoicesList.invoice.number')}</Typography>
            {errors?.number && errors?.number !== 'missing confidence' && (
              <Typography className="text-danger">{errors?.number}</Typography>
            )}
            <TextField
              fullWidth
              size="small"
              id="number"
              name="number"
              placeholder={intl.get('newInvoice.invoiceNumber')}
              value={input?.number}
              onChange={(e) => handleInputChange('number', e.target.value)}
              onBlur={(e) => { handleInputChange('number', e.target.value); handleFieldReview('number'); }}
              disabled={fieldReadOnly}
              error={!!errors?.number}
            />
          </Grid>
          <Grid
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          >
            <Typography gutterBottom>{intl.get('newInvoice.invoiceCurrency')}</Typography>
            {errors?.idCurrency && errors?.idCurrency !== 'missing confidence' && (
              <Typography className="text-danger">{errors?.idCurrency}</Typography>
            )}
            <Select
              fullWidth
              size="small"
              id="currency"
              name="currency"
              value={input?.idCurrency}
              displayEmpty
              disabled={fieldReadOnly}
              inputProps={{ placeholder: intl.get('newInvoice.invoiceCurrency') }}
              onChange={(e) => handleInputChange('idCurrency', e.target.value)}
              onBlur={(e) => { handleInputChange('idCurrency', e.target.value); handleFieldReview('idCurrency'); handleFieldReview('currency'); }}
              error={!!errors?.idCurrency}
            >
              <MenuItem disabled value="">
                <em>{intl.get('newInvoice.invoiceCurrency')}</em>
              </MenuItem>
              {currencies?.map((currency) => (
                <MenuItem key={currency.id} value={currency.id}>
                  {currency.code}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          >
            <Typography gutterBottom className={`${errors?.addressFrom ? 'text-danger' : ''}`}>
              {intl.get('newInvoice.invoiceAddressFrom')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              id="addressFrom"
              name="addressFrom"
              placeholder={intl.get('newInvoice.invoiceAddressFrom')}
              value={input?.addressFrom}
              onChange={(e) => handleInputChange('addressFrom', e.target.value)}
              onBlur={(e) => { handleInputChange('addressFrom', e.target.value); handleFieldReview('addressFrom'); }}
              disabled={fieldReadOnly}
              error={!!errors?.addressFrom}
            />
          </Grid>
          <Grid
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          >
            <Typography gutterBottom className={`${errors?.date ? 'text-danger' : ''}`}>
              {intl.get('newInvoice.date')}
            </Typography>
            <DemoContainer components={['DatePicker']}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  slotProps={{
                    openPickerIcon: { fontSize: 'small' },
                    textField: {
                      size: 'small',
                      error: !!errors?.date,
                    },
                  }}
                  disabled={fieldReadOnly}
                  value={new Date(input?.date)}
                  onChange={(e) => handleInputChange('date', e)}
                  onClose={() => handleFieldReview('date')}
                  sx={{
                    '& .MuiInputBase-root': {
                      overflow: 'hidden',
                      '&.Mui-error': {
                        border: '1px solid red !important',
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </DemoContainer>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          >
            <Autocomplete
              options={toOptions}
              getOptionLabel={(o) => o.name ?? ''}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              loading={toLoading}
              disabled={fieldReadOnly}
              value={toValue}
              inputValue={toInputValue}
              onInputChange={(_e, value, reason) => {
                if (reason === 'input') {
                  setToInputValue(value);
                  searchTo(value);
                } else if (reason === 'clear') {
                  setToInputValue('');
                  setToValue(null);
                }
              }}
              onChange={(_e, persona) => {
                if (!persona) return;
                setToValue(persona);
                setToInputValue(persona.name);
                handleInputChange('idTo', persona.id, { to: persona.name });
                handleFieldReview('idTo');
                handleFieldReview('personaTo');
                handleFieldReview('to');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={intl.get('newInvoice.to')}
                  error={!!errors?.idTo && errors?.idTo !== 'missing confidence'}
                  helperText={errors?.idTo && errors?.idTo !== 'missing confidence' ? errors.idTo : undefined}
                />
              )}
            />
          </Grid>
          <Grid
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          >
            <Typography gutterBottom className={`${errors?.addressTo ? 'text-danger' : ''}`}>
              {intl.get('newInvoice.invoiceAddressTo')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              id="addressTo"
              name="addressTo"
              placeholder={intl.get('newInvoice.invoiceAddressTo')}
              value={input?.addressTo}
              onChange={(e) => handleInputChange('addressTo', e.target.value)}
              onBlur={(e) => { handleInputChange('addressTo', e.target.value); handleFieldReview('addressTo'); }}
              disabled={fieldReadOnly}
              error={!!errors?.addressTo}
            />
          </Grid>
        </Grid>
      </Grid>
      <Divider sx={{ my: 3 }} />
      <Grid
        container
        spacing={2}
        size={{
          xl: 12,
          md: 6,
        }}
      >
        <Grid
          container
          className="justify-content-start"
          size={{
            xl: 6,
          }}
        >
          <Typography
            className={`${errors?.idInvoiceType ? 'text-danger' : ''}`}
            style={{ marginTop: '5px' }}
          >
            {intl.get('newInvoice.invoiceType')}{' '}
            {errors?.idInvoiceType && errors?.idInvoiceType !== 'missing confidence' && (
              <span className="text-danger">{errors?.idInvoiceType}</span>
            )}
          </Typography>
          <Grid
            size={{
              xl: 12,
            }}
          >
            <Select
              id="invoiceType"
              name="invoiceType"
              value={input?.idInvoiceType === 0 ? '' : input?.idInvoiceType}
              fullWidth
              displayEmpty
              disabled={fieldReadOnly}
              inputProps={{
                placeholder: intl.get('newInvoice.invoiceType'),
              }}
              onChange={(e) => handleInputChange('idInvoiceType', e.target.value)}
              onBlur={(e) => { handleInputChange('idInvoiceType', e.target.value); handleFieldReview('idInvoiceType'); }}
              error={errors?.idInvoiceType ? true : false}
            >
              <MenuItem disabled value="">
                <em>{intl.get('newInvoice.invoiceType')}</em>
              </MenuItem>
              {invoiceTypes?.map((invoiceType) => (
                <MenuItem key={invoiceType.id} value={invoiceType.id}>
                  {invoiceType.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
        <Grid
          container
          className="justify-content-end"
          size={{
            xl: 6,
          }}
        >
          <Grid
            container
            className="justify-content-start"
            size={{
              xl: 10,
            }}
          >
            <Typography
              className={`${errors?.paymentTerms ? 'text-danger' : ''}`}
              style={{ marginTop: '5px' }}
            >
              {intl.get('newInvoice.paymentTerms')}{' '}
              {errors?.paymentTerms && errors?.paymentTerms !== 'missing confidence' && (
                <span className="text-danger">{errors?.paymentTerms}</span>
              )}
            </Typography>
          </Grid>
          <Grid
            container
            className="justify-content-end"
            size={{
              xl: 10,
            }}
          >
            <TextField
              id="paymentTerms"
              name="paymentTerms"
              placeholder={intl.get('newInvoice.paymentTerms')}
              value={input?.paymentTerms}
              onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
              onBlur={(e) => { handleInputChange('paymentTerms', e.target.value); handleFieldReview('paymentTerms'); }}
              disabled={fieldReadOnly}
              fullWidth
              error={errors?.paymentTerms ? true : false}
            />
          </Grid>
        </Grid>
      </Grid>
      {errors?.invoiceItems && (
        <Grid
          container
          className="justify-content-start"
          style={{ marginTop: '10px', marginBottom: '-10px' }}
          size={{
            xl: 12,
          }}
        >
          <Typography className="text-danger">{errors?.invoiceItems}</Typography>
        </Grid>
      )}
      <Grid sx={{ marginTop: '20px', width: '100%', xl: 12 }}>
        <NewInvoiceItemTable disabled={fieldReadOnly} />
      </Grid>
      <Grid
        container
        style={{ marginTop: '10px' }}
        size={{
          xl: 12,
        }}
      >
        <Grid
          size={{
            xl: 6,
          }}
        ></Grid>
        <Grid sx={{ marginTop: '20px', width: '100%' }} size={{ xl: 6 }}>
          <NewInvoiceSummary disabled={fieldReadOnly} />
        </Grid>
      </Grid>
      <Grid className="justify-content-end">
        <Grid className="d-flex justify-content-end" sx={{ marginTop: '20px' }}>
          <WorkflowActionsBar
            viewMode=""
            onActionClick={handleSave}
            onBeforeAction={handleBeforeAction}
            id={null}
            stateId={input.idWorkflowState}
            loading={loading}
            disabled={onlyView}
            onSave={onlyView ? undefined : handleSaveOnly}
            saveDisabled={isDisabled() || fieldReadOnly}
            returnPath={workflow?.idModule === 4 ? '/accountspayable/all' : workflow?.idModule === 5 ? '/accountspayable' : undefined}
          />
        </Grid>
      </Grid>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{intl.get('accountsPayable.validate.confirmSubmit.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {intl.get('accountsPayable.validate.confirmSubmit.body')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setConfirmOpen(false);
            if (pendingSubmitArgs.current?.resolve) {
              pendingSubmitArgs.current.resolve(false);
              pendingSubmitArgs.current = null;
            }
          }}>
            {intl.get('accountsPayable.validate.confirmSubmit.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmOpen(false);
              if (pendingSubmitArgs.current?.resolve) {
                pendingSubmitArgs.current.resolve(true);
                pendingSubmitArgs.current = null;
              }
            }}
          >
            {intl.get('accountsPayable.validate.confirmSubmit.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <InvoiceReconciliationDialog
        open={reconcileOpen}
        invoiceId={input?.id ?? 0}
        invoiceAmount={Math.round((input?.amount ?? 0) * 100)}
        currency={currencies?.find((c) => c.id === input?.idCurrency)?.code ?? ''}
        incoming={0}
        onConfirm={handleReconcileConfirm}
        onCancel={handleReconcileCancel}
      />
    </>
  );
};

export default AccountsPayableForm;
